"use client"

import { type ReactNode, useEffect, useRef } from "react"

type Props = {
  className: string
  labelledBy: string
  testId?: string
  children: ReactNode
}

/**
 * Sezione che si rivela quando entra in vista. Gli attributi sono scritti sul
 * DOM, senza stato: con JS disattivato (o prima dell'idratazione) il contenuto
 * resta interamente visibile, e non c'è nessun re-render per l'animazione.
 */
export default function CollectionSmallReveal({
  className,
  labelledBy,
  testId = "home-collection-small",
  children,
}: Props) {
  const rootRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const node = rootRef.current

    if (!node) {
      return
    }

    if (
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      !("IntersectionObserver" in window)
    ) {
      node.setAttribute("data-in", "")
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) {
          return
        }

        node.setAttribute("data-in", "")
        observer.disconnect()
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.08 }
    )

    node.setAttribute("data-ready", "")
    observer.observe(node)

    return () => observer.disconnect()
  }, [])

  return (
    <section
      aria-labelledby={labelledBy}
      className={className}
      data-testid={testId}
      ref={rootRef}
    >
      {children}
    </section>
  )
}
